import * as React from 'react';
import { PrimaryButton, DefaultButton } from '@fluentui/react/lib/Button';
import { entityCriteriaScreen, summaryScreen } from '../actions/navigationWorker';

type Props = {
	link?: string;
	linkTitle?: string;
	hide?: boolean;
	message?: Error | string;
	options?: {
		hideTitle?: boolean;
		hideRetry?: boolean;
		backTitle?: string;
	};
};

function ErrorMessage({ link, message: err, linkTitle, hide, options }: Props) {
	const [isVisible, setIsVisible] = React.useState(false);

	React.useEffect(() => {
		// Fade in animation
		setTimeout(() => setIsVisible(true), 100);
	}, []);

	if (hide === true) return null;

	const message = err ? (typeof err === 'string' ? err : err.message) : undefined;

	const back_to_start = () => {
		entityCriteriaScreen(true);
	};

	const back_to_summary = () => {
		summaryScreen();
	};

	const errorIcon = (
		<div className="error-icon-wrapper" style={{
			display: 'inline-block',
			animation: isVisible ? 'scaleIn 0.5s ease-out' : 'none',
			transform: isVisible ? 'scale(1)' : 'scale(0)'
		}}>
			<svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
				<circle cx="40" cy="40" r="38" fill="#EF4444" stroke="#DC2626" strokeWidth="4"/>
				<path d="M40 25V45" stroke="white" strokeWidth="6" strokeLinecap="round"/>
				<circle cx="40" cy="55" r="3" fill="white"/>
			</svg>
		</div>
	);

	const modernStyles: React.CSSProperties = {
		opacity: isVisible ? 1 : 0,
		transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
		transition: 'all 0.5s ease-out',
		padding: '32px 24px',
		maxWidth: '500px',
		margin: '0 auto'
	};

	const headingStyle: React.CSSProperties = {
		fontSize: '20px',
		fontWeight: 600,
		color: '#1a1a1a',
		marginTop: '24px',
		marginBottom: '8px',
		lineHeight: '1.4'
	};

	const messageStyle: React.CSSProperties = {
		fontSize: '14px',
		color: '#DC2626',
		lineHeight: '1.6',
		marginBottom: '24px',
		fontWeight: 500
	};

	const linkStyle: React.CSSProperties = {
		fontSize: '14px',
		color: '#0078D4',
		textDecoration: 'underline',
		marginBottom: '24px',
		display: 'inline-block'
	};

	const timestampStyle: React.CSSProperties = {
		fontSize: '12px',
		color: '#999',
		marginBottom: '24px'
	};

	const buttonContainerStyle: React.CSSProperties = {
		display: 'flex',
		gap: '12px',
		justifyContent: 'center',
		marginTop: '24px',
		flexWrap: 'wrap'
	};

	const currentTime = new Date().toLocaleString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
		hour12: true
	});

	const header = options?.hideTitle ? null : (
		<>
			{errorIcon}
			<h2 style={headingStyle}>Error Filing Email</h2>
			<p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
				Unfortunately an error has occurred while filing the email to Xplan.
			</p>
		</>
	);

	if (link && message) {
		return (
			<div style={modernStyles}>
				<div style={{ textAlign: 'center' }}>
					{header}
					<p style={messageStyle}>{message}</p>
					<p style={timestampStyle}>Occurred on {currentTime}</p>
					<a href={link} target="_blank" rel="noreferrer" style={linkStyle}>
						{linkTitle || 'View Details'}
					</a>
					<div style={buttonContainerStyle}>
						{options?.hideRetry !== true && (
							<PrimaryButton
								data-automation-id="btnRetry"
								text="Retry"
								onClick={back_to_summary}
								styles={{
									root: { minWidth: '140px', height: '40px' }
								}}
							/>
						)}
						<DefaultButton
							data-automation-id="btnRestart"
							text={options?.backTitle || 'Restart'}
							onClick={back_to_start}
							styles={{
								root: { minWidth: '140px', height: '40px' }
							}}
						/>
					</div>
				</div>
			</div>
		);
	}

	if (message) {
		return (
			<div style={modernStyles}>
				<div style={{ textAlign: 'center' }}>
					{header}
					<p style={messageStyle}>{message}</p>
					<p style={timestampStyle}>Occurred on {currentTime}</p>
					<div style={buttonContainerStyle}>
						{options?.hideRetry !== true && (
							<PrimaryButton
								data-automation-id="btnRetry"
								text="Retry"
								onClick={back_to_summary}
								styles={{
									root: { 
										minWidth: '140px', 
										height: '40px',
										fontSize: '14px',
										fontWeight: 600,
										borderRadius: '8px',
										backgroundColor: '#5D3FD6',
										border: 'none'
									},
									rootHovered: {
										backgroundColor: '#4A2FC7'
									},
									rootPressed: {
										backgroundColor: '#3B24A8'
									}
								}}
							/>
						)}
						<DefaultButton
							data-automation-id="btnRestart"
							text={options?.backTitle || 'Restart'}
							onClick={back_to_start}
							styles={{
								root: { 
									minWidth: '140px', 
									height: '40px',
									fontSize: '14px',
									fontWeight: 600,
									borderRadius: '8px',
									backgroundColor: '#ffffff',
									color: '#5D3FD6',
									border: '2px solid #5D3FD6'
								},
								rootHovered: {
									backgroundColor: '#f8f6fc',
									color: '#4A2FC7',
									border: '2px solid #4A2FC7'
								},
								rootPressed: {
									backgroundColor: '#ede8f7',
									color: '#3B24A8',
									border: '2px solid #3B24A8'
								}
							}}
						/>
					</div>
				</div>
			</div>
		);
	}

	if (link) {
		return (
			<div style={modernStyles}>
				<div style={{ textAlign: 'center' }}>
					{header}
					<p style={timestampStyle}>Occurred on {currentTime}</p>
					<a href={link} target="_blank" rel="noreferrer" style={linkStyle}>
						{linkTitle || 'View Details'}
					</a>
					<div style={buttonContainerStyle}>
						{options?.hideRetry !== true && (
							<PrimaryButton
								data-automation-id="btnRetry"
								text="Retry"
								onClick={back_to_summary}
								styles={{
									root: { 
										minWidth: '140px', 
										height: '40px',
										fontSize: '14px',
										fontWeight: 600,
										borderRadius: '8px',
										backgroundColor: '#5D3FD6',
										border: 'none'
									},
									rootHovered: {
										backgroundColor: '#4A2FC7'
									},
									rootPressed: {
										backgroundColor: '#3B24A8'
									}
								}}
							/>
						)}
						<DefaultButton
							data-automation-id="btnRestart"
							text={options?.backTitle || 'Restart'}
							onClick={back_to_start}
							styles={{
								root: { 
									minWidth: '140px', 
									height: '40px',
									fontSize: '14px',
									fontWeight: 600,
									borderRadius: '8px',
									backgroundColor: '#ffffff',
									color: '#5D3FD6',
									border: '2px solid #5D3FD6'
								},
								rootHovered: {
									backgroundColor: '#f8f6fc',
									color: '#4A2FC7',
									border: '2px solid #4A2FC7'
								},
								rootPressed: {
									backgroundColor: '#ede8f7',
									color: '#3B24A8',
									border: '2px solid #3B24A8'
								}
							}}
						/>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div style={modernStyles}>
			<div style={{ textAlign: 'center' }}>
				{header}
				<p style={timestampStyle}>Occurred on {currentTime}</p>
				<div style={buttonContainerStyle}>
					{options?.hideRetry !== true && (
						<PrimaryButton
							data-automation-id="btnRetry"
							text="Retry"
							onClick={back_to_summary}
							styles={{
								root: { 
									minWidth: '140px', 
									height: '40px',
									fontSize: '14px',
									fontWeight: 600,
									borderRadius: '8px',
									backgroundColor: '#5D3FD6',
									border: 'none'
								},
								rootHovered: {
									backgroundColor: '#4A2FC7'
								},
								rootPressed: {
									backgroundColor: '#3B24A8'
								}
							}}
						/>
					)}
					<DefaultButton
						data-automation-id="btnRestart"
						text={options?.backTitle || 'Restart'}
						onClick={back_to_start}
						styles={{
							root: { 
								minWidth: '140px', 
								height: '40px',
								fontSize: '14px',
								fontWeight: 600,
								borderRadius: '8px',
								backgroundColor: '#ffffff',
								color: '#5D3FD6',
								border: '2px solid #5D3FD6'
							},
							rootHovered: {
								backgroundColor: '#f8f6fc',
								color: '#4A2FC7',
								border: '2px solid #4A2FC7'
							},
							rootPressed: {
								backgroundColor: '#ede8f7',
								color: '#3B24A8',
								border: '2px solid #3B24A8'
							}
						}}
					/>
				</div>
			</div>
		</div>
	);
}

export default ErrorMessage;
